<script setup lang="ts">
import GameCard from '~/components/gamescreen/GameCard.vue'
import GroupPhaseOrderInfo from '~/components/gamescreen/GroupPhaseOrderInfo.vue'
import { useUserPrintingAppStore } from '~/store/UserPrintingAppStore'

const { tournament, group, boards, displayOnly } = defineProps<{
  tournament: ManageTournament | Tournament
  group: TournamentGroup
  boards: Board[]
  displayOnly: boolean
}>()

const printingAppStore = useUserPrintingAppStore()

const shootoutOutcome = ref<Record<string, string | null>>({})

const { getError, setErrors } = useErrorHelper()
const { $api } = useNuxtApp()

const hasFreeBoards = computed(() => boards && boards.filter(board => board.free).length > 0)
const shootoutFormOpen = ref(false)
const boardPopupOpen = ref(false)

watch(shootoutFormOpen, () => {
  if (shootoutFormOpen.value) {
    toggleShootout()
  }
})

const toggleShootout = () => {
  if (Object.keys(group.shootout_outcome).length !== 0) {
    const flippedObject: typeof shootoutOutcome.value = {}

    for (const key in group.shootout_outcome) {
      const value = group.shootout_outcome[key]
      if (value) {
        flippedObject[value] = key
      }
    }
    shootoutOutcome.value = flippedObject
  }
  else {
    if (group.shootout_players) {
      if (group.shootout_players[0]) {
        shootoutOutcome.value[group.shootout_players[0].id] = null
      }
      if (group.shootout_players[1]) {
        shootoutOutcome.value[group.shootout_players[1].id] = null
      }
      if (group.shootout_players[2]) {
        shootoutOutcome.value[group.shootout_players[2].id] = null
      }
    }
  }
}

function saveShootout() {
  const flippedObject: typeof shootoutOutcome.value = {}

  for (const key in shootoutOutcome.value) {
    const value = shootoutOutcome.value[key]
    if (value) {
      flippedObject[value] = key
    }
  }

  $api('/tournaments/{tournament}/update-shootout-outcome', {
    method: 'POST',
    path: {
      tournament: tournament.id,
    },
    body: {
      group: group.group_number,
      outcome: flippedObject as unknown as Array<Array<number>>,
    },
  })
    .catch((e) => {
      if (e.status === 422) {
        setErrors(e.data.errors)
      }
      throw e
    })
    .then(() => {
      shootoutFormOpen.value = false
    })
}

function clearBoard(boardId: number) {
  $api('/tournaments/{tournament}/clear-group-board', {
    method: 'post',
    path: {
      tournament: tournament.id,
    },
    body: {
      board: boardId,
    },
  }).then(() => {
    boardPopupOpen.value = false
  })
}

function assignBoard(boardId: number, group: number) {
  $api('/tournaments/{tournament}/assign-group-board', {
    method: 'post',
    path: {
      tournament: tournament.id,
    },
    body: {
      board: boardId,
      group: group,
    },
  }).then(() => {
    boardPopupOpen.value = false
  })
}
</script>

<template>
  <div class="flex flex-col">
    <div class="!grid grid-cols-2 justify-between gap-2 items-center !flex-wrap">
      <div class="text-center flex items-center">
        <UiPopover>
          <UiPopoverTrigger>
            <div class="text-lg font-bold items-center flex gap-2">
              Group {{ group.label }} <Icon name="tabler-info-circle" />
            </div>
          </UiPopoverTrigger>
          <UiPopoverContent class="w-[32rem] max-w-full">
            <GroupPhaseOrderInfo
              :group="group"
              :games="group.games"
            />
          </UiPopoverContent>
        </UiPopover>
      </div>
      <div class="flex flex-row items-end">
        <UiDropdownMenu v-if="!displayOnly">
          <UiDropdownMenuTrigger as-child>
            <UiButton
              size="icon"
              variant="ghost"
              class="ml-auto"
            >
              <Icon name="tabler-printer" />
            </UiButton>
          </UiDropdownMenuTrigger>
          <UiDropdownMenuContent>
            <UiDropdownMenuItem
              @click="printingAppStore.directPrint(tournament.id, null, group.group_number)"
            >
              {{ $t('Print group sheet') }}
            </UiDropdownMenuItem>
            <UiDropdownMenuItem
              @click="printingAppStore.directPrint(tournament.id, null, group.group_number, true)"
            >
              {{ $t('Print individual matches') }}
            </UiDropdownMenuItem>
          </UiDropdownMenuContent>
        </UiDropdownMenu>

        <UiBadge
          v-if="displayOnly && group.board"
          class="ml-auto"
        >
          {{ group.board.name }}
        </UiBadge>
      </div>
    </div>
    <div
      class="flex flex-col rounded-lg p-2 border border-gray-300 dark:border-0 dark:bg-white/5 gap-1 text-sm max-w-full overflow-hidden"
    >
      <UiDropdownMenu
        v-if="!displayOnly && (hasFreeBoards || group.board) && !group.is_over"
        v-model:open="boardPopupOpen"
      >
        <UiDropdownMenuTrigger as-child>
          <UiButton
            size="xs"
            class="ml-auto"
            :variant="group.board ? 'warning' : 'success'"
          >
            <template v-if="!group.board && !group.is_over">
              {{ $t('Play group on board') }}
            </template>
            <template v-else-if="group.board">
              {{ group.board.name }}
            </template>
          </UiButton>
        </UiDropdownMenuTrigger>
        <UiDropdownMenuContent class="p-2">
          <div
            v-if="!group.board"
            class="grid grid-cols-4 gap-2"
          >
            <UiButton
              v-for="board in boards"
              :key="board.id"
              size="xs"
              :text="board.name"
              :disabled="!board.free || !board.available"
              @click="assignBoard(board.id, group.group_number)"
            />
          </div>
          <UiButton
            v-if="group.board"
            variant="destructive"
            :text="$t('Clear board')"
            @click="clearBoard(group.board.id)"
          />
          <div
            v-if="!group.board"
            class="text-sm"
          >
            {{ $t('This will assign the group to the board.') }}
            <br>
            {{ $t('During tablet play, the next game will automatically start.') }}
          </div>
        </UiDropdownMenuContent>
      </UiDropdownMenu>
      <div
        v-if="group.top_floor"
        class="grid grid-cols-2 gap-1"
      >
        <div class="flex flex-col">
          <strong>{{ $t('Winners') }}</strong>
          <div
            v-for="(player, i) in group.top_floor[0]"
            :key="`winners${i}`"
            class="max-w-full overflow-hidden truncate"
          >
            <template v-if="player.player.first_name || player.player.last_name">
              {{ i + 1 }}: {{ player.player.first_name }} {{ player.player.last_name }}
            </template>
            <template v-else>
              {{ i + 1 }}: {{ player.player.use_name }}
            </template>
          </div>
        </div>
        <div class="flex flex-col">
          <strong>{{ $t('Losers') }}</strong>
          <div
            v-for="(player, i) in group.top_floor[1]"
            :key="`losers${i}`"
            class="max-w-full overflow-hidden truncate"
          >
            <template v-if="player.player.first_name || player.player.last_name">
              {{ i + 1 }}: {{ player.player.first_name }} {{ player.player.last_name }}
            </template>
            <template v-else>
              {{ i + 1 }}: {{ player.player.use_name }}
            </template>
          </div>
        </div>
      </div>
      <UiDropdownMenu
        v-if="group.shootout_required && !displayOnly && group.shootout_players && tournament.phase === 'group'"
        v-model:open="shootoutFormOpen"
        @before-show="toggleShootout"
      >
        <UiDropdownMenuTrigger as-child>
          <UiButton
            :variant="Object.keys(group.shootout_outcome).length === 0 ? 'destructive': 'secondary'"
          >
            <template v-if="Object.keys(group.shootout_outcome).length === 0">
              <Icon name="tabler-exclamation-circle" />
              {{ $t('Enter shootout') }}
            </template>
            <template v-else>
              {{ $t('Update shootout') }}
            </template>
          </UiButton>
        </UiDropdownMenuTrigger>
        <UiDropdownMenuContent>
          <div class="flex flex-col gap-4">
            <template
              v-for="shootout_player in group.shootout_players"
              :key="shootout_player.id"
            >
              <div class="flex flex-col">
                {{ shootout_player.name }}
                <UiSelect
                  v-model="shootoutOutcome[shootout_player.id]"
                  class="w-40"
                >
                  <UiSelectTrigger>
                    <UiSelectValue :placeholder="$t('Select outcome')" />
                  </UiSelectTrigger>
                  <UiSelectContent>
                    <UiSelectGroup>
                      <UiSelectItem
                        v-for="option in [
                          { value: '0', label: $t('first') },
                          { value: '1', label: $t('second') },
                          { value: '2', label: $t('third') },
                        ]"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ option.label }}
                      </UiSelectItem>
                    </UiSelectGroup>
                  </UiSelectContent>
                </UiSelect>
              </div>
            </template>
            <UiAlert
              v-if="getError('outcome')"
              variant="warning"
              :title="getError('outcome')"
            />
            <div class="flex flex-row justify-end mt-2 w-full">
              <UiButton
                type="button"
                variant="success"
                @click="saveShootout()"
              >
                {{ $t('Save') }}
              </UiButton>
            </div>
          </div>
        </UiDropdownMenuContent>
      </UiDropdownMenu>
      <GameCard
        v-for="game in group.games"
        :key="game.id"
        :game="game"
        :for-display="displayOnly"
        :boards="boards"
      />
    </div>
  </div>
</template>
